// Squad — a team's roster (Name / Pos / Nat / Age / HOPS), ported 1:1 from
// js/renderers-squads.js via the 04b bridge. Competition + team pickers, the "Table"
// switch to League Table (the reverse of League Table's row-click-to-Squad), the
// sticky-player default, and the static # rank column. Data via the new Phase-04
// useSquad seam.
//
// Interactive column-sorting (04g-2): via the shared useSortableRows hook — Name sorts
// by surname, Pos/Nat as text, Age/HOPS numeric; click `#` to restore. The default
// name-sorted order is unchanged.
//
// Two sources, one body: the snapshot (teams by NAME — it has no team ids), or on ?live=1
// real data (T2-10b, live/squad.ts — teams by id, B1). `_squadsTeam` holds a name on the
// snapshot site and an id on ?live=1.

import { leagueSelectGroups } from "./CountryFlag.js";
import { ModuleExplainer } from "./ModuleExplainer.js";
import { explainSquad } from "./explain.js";
import { useEffect, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import type { ProtoSquadPlayer } from "@catbento/shared";
import { type ProtoModuleProps, getModState, setModState, switchModuleType } from "./bridge.js";
import { SubControls } from "./SubControls.js";
import { StyledSelect, type StyledSelectItem } from "./StyledSelect.js";
import { useLeagueCompetitions, useLiveLeagueChoices } from "../hooks/useLeagueTable.js";
import { useSquad, useSquadTeams } from "../hooks/useSquad.js";
import { usePlayerBio } from "../hooks/usePlayerBio.js";
import { positionAbbrev, ageFromDob, formatHops, resolveSquadCompetition, resolveSquadTeam } from "./squad-view.js";
import { type SortColumn } from "./sortable-rows.js";
import { useSortableRows } from "./useSortableRows.js";
import { getStickyPlayer, setStickyPlayer } from "../canvas/sticky-player.js";
import { readSnapshot } from "../data/snapshot.js";
import { playerNameOf, type PlayerId } from "../data/players.js";
import { competitionIdOf, getLeagueIdOfName, type CompetitionId, type LeagueChoice } from "../data/league.js";
import { startPlayerTokenDrag } from "../canvas/menu-drag.js";
import { setStickyTeam } from "../canvas/sticky-team.js";
import type { TeamRef } from "../live/team.js";
import { LIVE } from "../live/flag.js";
import { liveApi } from "../live/api.js";
import { useLiveQuery } from "../live/query.js";
import { loadSquad, loadSquadTeams, loadStickyClub, storedTeamKey } from "../live/squad.js";
import { noteRowPlayer } from "../live/player-gender.js";
import { useFollowedCompetition } from "../hooks/useFollowedCompetition.js";

// Module-level (stable identity). get returns each cell's displayed value, so the sort
// matches the legacy's read-from-the-cell behaviour (Name surname, Age/HOPS numeric,
// "—" gaps sink). Order matches the header cells below.
const SQUAD_COLUMNS: SortColumn<ProtoSquadPlayer>[] = [
  { label: "Name", get: (p) => p.shortName },
  { label: "Pos", get: (p) => positionAbbrev(p.positionTemplate) },
  { label: "Nat", get: (p) => p.country || "—" },
  { label: "Age", get: (p) => ageFromDob(p.dob) ?? "—" },
  { label: "HOPS", get: (p) => formatHops(p.hopsRating) },
];

const EMPTY_SQUAD: ProtoSquadPlayer[] = [];

export function SquadModule(props: ProtoModuleProps) {
  return LIVE ? <LiveSquad {...props} /> : <SnapshotSquad {...props} />;
}

function SnapshotSquad({ mod, body }: ProtoModuleProps) {
  const competitions = useLeagueCompetitions();

  // Sticky-player default (same convention every freshly-placed module honours):
  // seed competition/team from the last-picked player's own team when available.
  // usePlayerBio still takes the snapshot name (it goes by id in the Bio slice).
  const snapshot = readSnapshot();
  const stickyName = playerNameOf(snapshot.players, getStickyPlayer());
  const stickyBio = usePlayerBio(stickyName ?? "");
  // Her league is a name in the snapshot → its id. Null for a league the snapshot has no id
  // for (Liga F, Frauen Bundesliga) — it has no table either, so Squad opens on the first.
  const stickyComp = stickyBio?.competition ? getLeagueIdOfName(snapshot, stickyBio.competition) : null;
  const stickyTeam = stickyBio?.team ?? null;

  const [competition, setCompetition] = useState<CompetitionId | null>(() =>
    resolveSquadCompetition(getModState<CompetitionId>(mod, "_squadsCompetition"), competitions, stickyComp),
  );
  const teams = useSquadTeams(competition);
  const [team, setTeam] = useState<string | null>(() =>
    resolveSquadTeam(getModState<string>(mod, "_squadsTeam"), teams, competition, stickyComp, stickyTeam),
  );

  // team can be stale after a competition switch (setTeam(null)) — resolve it, still
  // honouring the sticky-team preference when its competition matches.
  const effectiveTeam = resolveSquadTeam(team, teams, competition, stickyComp, stickyTeam);
  const squad = useSquad(competition, effectiveTeam ?? "");

  useEffect(() => {
    if (competition !== null) setModState(mod, "_squadsCompetition", competition);
    setModState(mod, "_squadsTeam", effectiveTeam || undefined);
  }, [mod, competition, effectiveTeam]);

  return (
    <SquadBody
      mod={mod}
      body={body}
      competitions={competitions}
      competition={competition}
      onCompetition={(value) => {
        setCompetition(competitionIdOf(value));
        setTeam(null); // a team from the old competition has no meaning here
      }}
      teamItems={teams.map((t) => ({ value: t, label: t }))}
      team={effectiveTeam}
      onTeam={setTeam}
      teamLabel={effectiveTeam}
      squad={squad}
    />
  );
}

/** Real data: any league in the DB, its clubs by id. The answers are the widget's own
 *  (live/query.ts); a league or club switch asks only for what's new. */
function LiveSquad({ mod, body }: ProtoModuleProps) {
  const competitions = useLiveLeagueChoices();
  // Sticky-player default, as the snapshot path: her current club, from the API.
  const sticky = getStickyPlayer();
  const stickyClub = useLiveQuery(sticky === null ? null : `squad:sticky:${sticky}`, () =>
    loadStickyClub(liveApi(), sticky as PlayerId),
  );
  const stickyComp = stickyClub?.competitionId ?? null;
  const stickyTeam = stickyClub?.teamId == null ? null : String(stickyClub.teamId);

  // The one it had, else the player's league — or, selected during a linked pick, the new
  // player's league (useFollowedCompetition; same order as resolveSquadCompetition).
  const opening = useFollowedCompetition(mod, "_squadsCompetition", competitions, stickyComp);
  const [competition, setCompetition] = useState<CompetitionId | null>(opening.competition);
  const league = competitions.find((c) => c.id === competition) ?? null;
  const clubs = useLiveQuery(league === null ? null : `squad:teams:${league.id}`, () =>
    loadSquadTeams(liveApi(), league as LeagueChoice),
  );
  const teamKeys = clubs ? clubs.teams.map((t) => String(t.id)) : [];
  // Teams are keyed by String(id) here (the picker's values); stored as the id. After
  // following a player the old club is dropped, so the player's club wins.
  const [team, setTeam] = useState<string | null>(() =>
    resolveSquadTeam(
      clubs && !opening.followed ? storedTeamKey(getModState<unknown>(mod, "_squadsTeam"), clubs.teams) : null,
      teamKeys,
      competition,
      stickyComp,
      stickyTeam,
    ),
  );
  const effectiveTeam = resolveSquadTeam(team, teamKeys, competition, stickyComp, stickyTeam);
  const seasonId = clubs?.seasonId ?? null;
  const squad =
    useLiveQuery(
      effectiveTeam === null || competition === null || seasonId === null
        ? null
        : `squad:players:${effectiveTeam}:${competition}:${seasonId}`,
      () => loadSquad(liveApi(), Number(effectiveTeam), competition as CompetitionId, seasonId as number),
    ) ?? EMPTY_SQUAD;

  const teamLabel = clubs?.teams.find((t) => String(t.id) === effectiveTeam)?.name ?? null;

  useEffect(() => {
    if (competition !== null) setModState(mod, "_squadsCompetition", competition);
    setModState(mod, "_squadsTeam", effectiveTeam === null ? undefined : Number(effectiveTeam));
  }, [mod, competition, effectiveTeam]);

  return (
    <SquadBody
      mod={mod}
      body={body}
      competitions={competitions}
      competition={competition}
      // A pick is saved to the `_`-state at once, not when its data lands (the T2-1 pattern): an
      // error rebuilds the widget from that state, so Retry then retries the pick, not the old club.
      onCompetition={(value) => {
        const next = competitionIdOf(value);
        setModState(mod, "_squadsCompetition", next);
        setModState(mod, "_squadsTeam", undefined);
        setCompetition(next);
        setTeam(null); // a team from the old competition has no meaning here
      }}
      teamItems={(clubs?.teams ?? []).map((t) => ({ value: String(t.id), label: t.name }))}
      team={effectiveTeam}
      onTeam={(value) => {
        setModState(mod, "_squadsTeam", Number(value));
        setTeam(value);
        // The next new team module opens on it (canvas/sticky-team.ts).
        const club = clubs?.teams.find((t) => String(t.id) === value);
        if (LIVE && club && competition !== null) setStickyTeam({ id: club.id, name: club.name, competitionId: competition, season: null });
      }}
      teamLabel={teamLabel}
      squad={squad}
      teamRef={
        effectiveTeam !== null && teamLabel !== null && competition !== null
          ? { id: Number(effectiveTeam), name: teamLabel, competitionId: competition, season: seasonId === null ? null : String(seasonId) }
          : null
      }
      noteRow={(p) => {
        // Rows only show once a league is picked, so there's always one here.
        if (league === null) throw new Error("Squad: a row dragged with no league picked");
        noteRowPlayer({ id: p.playerId, name: p.name, team: teamLabel }, league.womens, p.positionTemplate);
      }}
    />
  );
}

interface SquadBodyProps {
  mod: HTMLElement;
  body: HTMLElement;
  competitions: LeagueChoice[];
  competition: CompetitionId | null;
  onCompetition: (value: string) => void;
  teamItems: StyledSelectItem[];
  /** The picker's value (a name on the snapshot site, String(id) on ?live=1). */
  team: string | null;
  onTeam: (value: string) => void;
  /** The club's name, for the empty message. */
  teamLabel: string | null;
  squad: ProtoSquadPlayer[];
  /** Live only: notes a row's player (name + her league's gender) when it's dragged — a live
   *  Stats tile takes her only once her gender is known. */
  noteRow?: (p: ProtoSquadPlayer) => void;
  /** Live only: the club shown, as a team to pick up (teams and matches, 2026-10-30). */
  teamRef?: TeamRef | null;
}

function SquadBody({ mod, body, competitions, competition, onCompetition, teamItems, team, onTeam, teamLabel, squad, noteRow, teamRef }: SquadBodyProps) {
  // Sort hook — called unconditionally (before the early returns).
  const { rows: sortedSquad, headerProps, rankProps, containerRef } = useSortableRows(
    squad.length ? squad : EMPTY_SQUAD,
    SQUAD_COLUMNS,
  );

  useEffect(() => {
    body.style.justifyContent = competitions.length ? "flex-start" : "";
  }, [body, competitions.length]);

  // ?live=1: the team it shows, for dragging the whole Squad onto a module that shows teams (Ben,
  // 2026-10-31: it replaced a club handle beside Table; canvas/team-modules.ts heldTeamDrop).
  useEffect(() => {
    setModState(mod, "_squadTeamRef", teamRef ?? undefined);
  }, [mod, teamRef]);

  if (!competitions.length) {
    return <div className="stats-empty">No competition data captured.</div>;
  }

  const onTable = () => {
    setModState(mod, "_squadsReturnTitle", undefined);
    setModState(mod, "_leagueTableCompetition", competition);
    switchModuleType(mod, "League Table");
  };
  // A squad row is a drag source: dragging one onto empty canvas opens a Bio, onto a
  // Radar/Stats joins the comparison (startPlayerTokenDrag resolves the target), same as
  // the legacy renderer (renderers-squads.js:219).
  const onRowPointerDown = (e: ReactPointerEvent<HTMLDivElement>, p: ProtoSquadPlayer) => {
    if (window.innerWidth <= 768) return;
    e.preventDefault();
    e.stopPropagation();
    noteRow?.(p);
    setStickyPlayer(p.playerId);
    startPlayerTokenDrag(e.nativeEvent);
  };

  const subControls: ReactNode = (
    <SubControls mod={mod} body={body}>
      <StyledSelect
        // Live: grouped and searchable — top leagues, then by country, with flags (Ben, 2026-10-18).
        items={LIVE ? leagueSelectGroups(competitions, competition) : competitions.map((c) => ({ value: String(c.id), label: c.label }))}
        value={competition === null ? "" : String(competition)}
        onChange={onCompetition}
        wrapClass="squads-competition-select-wrap"
        extraClass="squads-competition-select"
        searchable={LIVE}
      />
      <StyledSelect
        items={teamItems}
        value={team ?? ""}
        onChange={onTeam}
        wrapClass="squads-team-select-wrap"
        extraClass="squads-team-select"
        searchable={teamItems.length > 12}
      />
      <button type="button" className="module-switch-btn" onClick={onTable}>
        Table
      </button>
    </SubControls>
  );

  if (!squad.length) {
    return (
      <>
        {subControls}
        <div className="stats-empty">No roster captured for {teamLabel}.</div>
      </>
    );
  }

  return (
    <>
      {LIVE && (
        <ModuleExplainer
          mod={mod}
          paragraphs={explainSquad({
            team: teamLabel,
            competition: competitions.find((c) => c.id === competition)?.label ?? null,
            count: squad.length,
          })}
        />
      )}
      {subControls}
      <div className="data-wrapper top-align" ref={containerRef}>
        <div className="data-row header">
          <div className="col-rank" onClick={rankProps.onClick}>#</div>
          <div className={`col-team ${headerProps(0).className}`.trim()} onClick={headerProps(0).onClick}>Name</div>
          <div className={`col-num ${headerProps(1).className}`.trim()} onClick={headerProps(1).onClick}>Pos</div>
          <div className={`col-num ${headerProps(2).className}`.trim()} onClick={headerProps(2).onClick}>Nat</div>
          <div className={`col-num ${headerProps(3).className}`.trim()} onClick={headerProps(3).onClick}>Age</div>
          <div className={`col-num pts ${headerProps(4).className}`.trim()} onClick={headerProps(4).onClick}>HOPS</div>
        </div>
        {sortedSquad.map((p, i) => (
          <div
            key={p.playerId}
            data-sort-key={p.name}
            className="data-row squads-row"
            onPointerDown={(e) => onRowPointerDown(e, p)}
          >
            <div className="col-rank">{i + 1}</div>
            <div className="col-team">{p.shortName}</div>
            <div className="col-num">{positionAbbrev(p.positionTemplate)}</div>
            <div className="col-num nowrap-text">{p.country || "—"}</div>
            <div className="col-num">{ageFromDob(p.dob) ?? "—"}</div>
            <div className="col-num pts">{formatHops(p.hopsRating)}</div>
          </div>
        ))}
      </div>
    </>
  );
}
